import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';

/**
 * Public /join page — lightweight email capture for visitors who haven't
 * made an account yet but want to be counted as a founding-member prospect.
 * Signed-in users get redirected straight to /profile so they can flip
 * the flag there (no repeat email-entry).
 */
export default function JoinPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    // If they're already signed in, skip the email capture and take them
    // straight to their saved readings — that's the most valuable place
    // for a returning founding member to land.
    if (user) navigate('/saved', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/founding/join`, { email: email.trim(), name: name.trim() || null });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="join-page">
      <div className="auth-page-inner">
        <button
          type="button"
          className="auth-page-back"
          onClick={() => navigate(-1)}
          data-testid="join-back-btn"
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
          <span>Back</span>
        </button>

        <div className="auth-page-card">
          <div className="auth-page-heading">
            <span className="founding-badge" style={{ margin: '0 0 16px' }} data-testid="join-badge">
              <span aria-hidden="true">✦</span>
              <span>100 founding spots only</span>
            </span>
            <h1>Become a founding member</h1>
            <p>
              Lock in your founding-member rate for life, unlock the Phase 2 reading when it launches, and help shape what we build next.
            </p>
          </div>

          {done ? (
            <div className="auth-success" data-testid="join-success">
              <strong>You're in.</strong>
              <p>
                Thank you for standing with us while this grows. We'll be in touch with the next chapter — and your founding-member rate is locked in for life.
              </p>
              <button type="button" className="auth-link" onClick={() => navigate('/tool')} data-testid="join-to-tool">
                Start your first reading →
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="join-form">
              {error && <div className="auth-error" data-testid="join-error">{error}</div>}
              <label className="field-label">Your name <span className="field-hint-inline">(optional)</span></label>
              <input
                type="text"
                className="field-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="First name"
                data-testid="join-name"
              />
              <label className="field-label">Email</label>
              <input
                type="email"
                className="field-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="join-email"
              />
              <button type="submit" className="auth-submit" disabled={loading || !email.trim()} data-testid="join-submit">
                {loading ? 'Saving…' : 'Count me in'}
              </button>
              <p className="auth-fineprint">
                We'll email you the moment the next chapter is ready — never any spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
